import IMask from 'imask';

const { initializeBirthchartForm } = require('./birthchart-form-controller.js');

document.addEventListener('DOMContentLoaded', () => {
    if (document.querySelector('[data-birthchart-form][data-react-managed]')) {
        return;
    }

    const gtagFunction = typeof gtag === 'function' ? gtag : null;
    const controller = initializeBirthchartForm(document, { gtagFunction });

    if (controller === null) {
        return;
    }

    const {
        dateInput,
        locationInput,
        timeInput,
    } = controller.elements;
    const locationIcon = controller.form.querySelector('#locationIcon');
    const spinner = controller.form.querySelector('#loadingSpinner');
    const locationFeedback = controller.form.querySelector('[data-location-feedback]');
    const serverError = controller.form.querySelector('[data-birthchart-server-error]');
    const locationRequestMethod = controller.form.dataset.locationRequestMethod === 'POST'
        ? 'POST'
        : 'GET';

    const awesompleteInstance = new Awesomplete(locationInput, {
        minChars: 3,
        filter: () => true,
        sort: false,
        item(text, input, index) {
            const listItem = document.createElement('li');
            listItem.setAttribute('aria-selected', 'false');
            listItem.setAttribute('role', 'option');
            listItem.id = `awesomplete_list_${this.count}_item_${index}`;
            listItem.classList.add('am-location-option');

            const content = document.createElement('span');
            content.classList.add('am-location-option__content');

            const title = document.createElement('strong');
            title.classList.add('am-location-option__title');
            title.textContent = String(text.value.name ?? '');

            const description = document.createElement('span');
            description.classList.add('am-location-option__description');
            description.textContent = String(text.value.description ?? '');

            content.appendChild(title);
            content.appendChild(description);
            listItem.appendChild(content);

            return listItem;
        },
    });

    locationInput.setAttribute('aria-autocomplete', 'list');
    locationInput.setAttribute('aria-controls', awesompleteInstance.ul.id);
    if (locationFeedback !== null) {
        awesompleteInstance.status.removeAttribute('role');
        awesompleteInstance.status.removeAttribute('aria-live');
        awesompleteInstance.status.setAttribute('aria-hidden', 'true');
        awesompleteInstance.status.textContent = '';
    }

    let locationSearchStarted = false;
    let lastSearchResults = [];
    let lastSearchQuery = '';
    const supportsAbortController = typeof AbortController !== 'undefined';
    let abortController = null;
    let debounceTimer = null;
    let requestSequence = 0;

    function setLocationFeedback(message = '', state = '') {
        if (locationFeedback === null) {
            return;
        }

        locationFeedback.textContent = message;

        if (state === '') {
            locationFeedback.removeAttribute('data-state');
        } else {
            locationFeedback.setAttribute('data-state', state);
        }

        if (message === '') {
            locationFeedback.setAttribute('hidden', '');
        } else {
            locationFeedback.removeAttribute('hidden');
        }
    }

    function showLocationLoading() {
        spinner?.classList.remove('hidden');
        locationIcon?.classList.add('hidden');
        locationInput.setAttribute('aria-busy', 'true');
        setLocationFeedback('Buscando cidades…', 'loading');
    }

    function hideLocationLoading() {
        spinner?.classList.add('hidden');
        locationIcon?.classList.remove('hidden');
        locationInput.setAttribute('aria-busy', 'false');
    }

    function requestConfiguration(query, signal) {
        if (locationRequestMethod === 'POST') {
            return {
                url: '/api/location',
                options: {
                    method: 'POST',
                    credentials: 'same-origin',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({ query }),
                    signal,
                },
            };
        }

        return {
            url: `/api/location?query=${encodeURIComponent(query)}`,
            options: { signal },
        };
    }

    locationInput.addEventListener('focusin', () => {
        if (!locationSearchStarted) {
            controller.trackLocationSearchStart();
            locationSearchStarted = true;
        }

        if (
            lastSearchResults.length > 0
            && locationInput.value.length >= 3
            && locationInput.value === lastSearchQuery
        ) {
            awesompleteInstance.list = lastSearchResults;
            awesompleteInstance.evaluate();
        }
    });

    locationInput.addEventListener('awesomplete-open', () => {
        const resultCount = awesompleteInstance.ul.children.length;
        setLocationFeedback(
            resultCount === 1
                ? '1 cidade encontrada. Use as setas para escolher.'
                : `${resultCount} cidades encontradas. Use as setas para escolher.`,
            'results',
        );
    });

    locationInput.addEventListener('awesomplete-highlight', (event) => {
        const selectedIndex = awesompleteInstance.index + 1;
        const resultCount = awesompleteInstance.ul.children.length;
        const label = String(event.text?.label ?? 'Cidade');
        setLocationFeedback(
            `${label}. Opção ${selectedIndex} de ${resultCount}.`,
            'results',
        );
    });

    locationInput.addEventListener('input', () => {
        const query = locationInput.value.trim();
        const requestId = ++requestSequence;

        awesompleteInstance.list = [];
        lastSearchResults = [];
        lastSearchQuery = '';

        if (supportsAbortController && abortController) {
            abortController.abort();
            abortController = null;
        }

        if (debounceTimer) {
            clearTimeout(debounceTimer);
            debounceTimer = null;
        }

        if (query.length >= 3) {
            showLocationLoading();

            debounceTimer = setTimeout(() => {
                controller.trackLocationSearch();

                let signal;
                let requestAbortController = null;
                if (supportsAbortController) {
                    requestAbortController = new AbortController();
                    abortController = requestAbortController;
                    signal = requestAbortController.signal;
                }

                const request = requestConfiguration(query, signal);

                fetch(request.url, request.options)
                    .then((response) => {
                        if (!response.ok) {
                            throw new Error('Location search request failed');
                        }

                        return response.json();
                    })
                    .then((data) => {
                        if (requestId !== requestSequence) {
                            return;
                        }

                        if (!Array.isArray(data)) {
                            throw new Error('Location search returned an invalid response');
                        }

                        const results = data.map((item) => {
                            const suggestion = {
                                label: `${item.name} - ${item.description}`,
                                value: item,
                            };
                            Object.assign(suggestion, item);

                            return suggestion;
                        });

                        lastSearchResults = results;
                        lastSearchQuery = query;
                        awesompleteInstance.list = results;
                        hideLocationLoading();

                        if (results.length === 0) {
                            setLocationFeedback(
                                'Nenhuma cidade foi encontrada. Confira a grafia ou tente outro nome.',
                                'empty',
                            );
                        } else {
                            setLocationFeedback(
                                results.length === 1
                                    ? '1 cidade encontrada. Use as setas para escolher.'
                                    : `${results.length} cidades encontradas. Use as setas para escolher.`,
                                'results',
                            );
                        }

                        if (abortController === requestAbortController) {
                            abortController = null;
                        }
                    })
                    .catch((error) => {
                        if (requestId !== requestSequence) {
                            return;
                        }

                        if (error.name !== 'AbortError') {
                            hideLocationLoading();
                            setLocationFeedback(
                                'Não foi possível buscar cidades agora. Confira sua conexão e tente novamente.',
                                'error',
                            );
                        }

                        if (abortController === requestAbortController) {
                            abortController = null;
                        }
                    });
            }, 300);
        } else {
            hideLocationLoading();

            if (query.length === 0) {
                setLocationFeedback('', '');
            } else {
                setLocationFeedback(
                    'Digite ao menos 3 letras para buscar uma cidade.',
                    'guidance',
                );
            }
        }
    });

    locationInput.addEventListener('awesomplete-selectcomplete', (event) => {
        if (controller.selectLocation(event.text)) {
            controller.trackLocationSelect();
            setLocationFeedback('Cidade selecionada.', 'success');
        }
    });

    IMask(dateInput, {
        mask: 'DD/MM/YYYY',
        blocks: {
            DD: {
                mask: IMask.MaskedRange,
                from: 1,
                to: 31,
                maxLength: 2,
            },
            MM: {
                mask: IMask.MaskedRange,
                from: 1,
                to: 12,
                maxLength: 2,
            },
            YYYY: {
                mask: IMask.MaskedRange,
                from: 1800,
                to: 2100,
                maxLength: 4,
            },
        },
        lazy: false,
    });

    IMask(timeInput, {
        mask: 'HH:mm',
        blocks: {
            HH: {
                mask: IMask.MaskedRange,
                from: 0,
                to: 23,
                maxLength: 2,
            },
            mm: {
                mask: IMask.MaskedRange,
                from: 0,
                to: 59,
                maxLength: 2,
            },
        },
        lazy: false,
    });

    if (serverError !== null) {
        serverError.focus();
    }

    window.addEventListener('pageshow', () => {
        controller.resetSubmissionState();
    });
});
